import { useState } from "react";
import { Box, Button, Flex, Text, TextField } from "@radix-ui/themes";
import { CopyIcon, CheckIcon, ReloadIcon } from "@radix-ui/react-icons";
import { useManagerContext } from "./context";
import type { BaseFieldProps } from "./FormFields";
import { generatePassword, validatePassword } from "./password-utils";

type PasswordFieldProps = BaseFieldProps & {
  name?: string;
  label?: string;
  placeholder?: string;
};

export function PasswordField({
  name = "password",
  label = "Password",
  placeholder = "Enter password",
}: PasswordFieldProps) {
  const { formValues, setFormValue } = useManagerContext();
  const [copied, setCopied] = useState(false);

  const password = (formValues[name] as string) ?? "";
  const passwordValidation = validatePassword(password);

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    setFormValue(name, newPassword);
    setCopied(false);
  };

  const handleCopyPassword = async () => {
    await navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box>
      <Text as="div" size="2" mb="1" weight="bold">
        {label} *
      </Text>
      <Flex gap="2" mb="1">
        <TextField.Root
          style={{ flex: 1 }}
          name={name}
          value={password}
          onChange={(e) => {
            setFormValue(name, e.target.value);
            setCopied(false);
          }}
          placeholder={placeholder}
        />
        <Button
          type="button"
          variant="soft"
          onClick={handleGeneratePassword}
          title="Generate password"
        >
          <ReloadIcon />
        </Button>
        <Button
          type="button"
          variant="soft"
          onClick={handleCopyPassword}
          disabled={!password}
          title="Copy password"
        >
          {copied ? <CheckIcon /> : <CopyIcon />}
        </Button>
      </Flex>
      {password && !passwordValidation.valid && (
        <Text size="1" color="red">
          {passwordValidation.errors.join(", ")}
        </Text>
      )}
    </Box>
  );
}
